import type { ColumnsType } from 'antd/es/table';
import type { Key, MouseEvent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import TruncatedTooltip from '../../components/Common/TruncatedTooltip';
import FilesTableActionsCell from '../../components/Files/FilesTableActionsCell';
import FilesTableCreatedByCell from '../../components/Files/FilesTableCreatedByCell';
import FilesTableDeletionDateCell from '../../components/Files/FilesTableDeletionDateCell';
import FilesTableFileSizeCell from '../../components/Files/FilesTableFileSizeCell';
import FilesTableLastModifiedCell from '../../components/Files/FilesTableLastModifiedCell';
import FilesTableSharedByCell from '../../components/Files/FilesTableSharedByCell';
import FilesTableTagsCell from '../../components/Files/FilesTableTagsCell';
import FilesTableTitleCell from '../../components/Files/FilesTableTitleCell';
import {
  FILES_TABLE_COLUMN_RESPONSIVE,
  FILES_TABLE_COLUMN_WIDTH_PERCENT,
  FILES_TABLE_TITLE_MIN_WIDTH,
} from '../../components/Files/FilesTableColumns/filesTableColumnsConfig';
import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import type File from '../../interfaces/File';
import type FileAction from '../../interfaces/FileAction';
import type { FileOffline } from '../../interfaces/FileApiService';

export interface UseFilesTableColumnsParams {
  filesOffline: FileOffline[];
  handleFileMenuAction: (filesIds: number[], action: FileMenuActionEnum) => void;
  handleRowContextMenu: (file: File, event: MouseEvent) => void;
  selectedTableKeys: Key[];
  setSelectedTableKeys: (keys: Key[]) => void;
  table: FilesTableEnum;
  topBarFileActions: FileAction[];
}

const TABLES_WITH_USER_COLUMNS = new Set<FilesTableEnum>([
  FilesTableEnum.Favorites,
  FilesTableEnum.Hidden,
  FilesTableEnum.Received,
]);

/**
 * Shared Ant Design table columns for all file list routes.
 * Columns degrade by breakpoint; title keeps inline actions below `md`.
 * Secondary columns use % widths; Title has no width so it absorbs leftover space.
 */
export function useFilesTableColumns({
  filesOffline,
  handleFileMenuAction,
  handleRowContextMenu,
  selectedTableKeys,
  setSelectedTableKeys,
  table,
  topBarFileActions,
}: UseFilesTableColumnsParams): ColumnsType<File> {
  const { t } = useTranslation();

  return useMemo(() => {
    const renderColumnTitle = (label: string) => (
      <TruncatedTooltip title={label} className="block max-w-full min-w-0">
        {label}
      </TruncatedTooltip>
    );

    const actionsCellProps = {
      filesActions: topBarFileActions,
      handleFileMenuAction,
      handleRowContextMenu,
      selectedTableKeys,
      setSelectedTableKeys,
    };

    const inlineActionsProps = {
      ...actionsCellProps,
    };

    const showUserColumns = TABLES_WITH_USER_COLUMNS.has(table);
    const isTrashTable = table === FilesTableEnum.Trash;
    const columnWidths = showUserColumns
      ? FILES_TABLE_COLUMN_WIDTH_PERCENT.withUsers
      : FILES_TABLE_COLUMN_WIDTH_PERCENT.compact;

    const columns: ColumnsType<File> = [
      {
        dataIndex: 'title',
        defaultSortOrder: 'ascend',
        ellipsis: true,
        key: 'title',
        minWidth: FILES_TABLE_TITLE_MIN_WIDTH,
        render: (_, record) => (
          <FilesTableTitleCell
            file={record}
            filesOffline={filesOffline}
            inlineActions={inlineActionsProps}
          />
        ),
        sorter: true,
        title: renderColumnTitle(t('common.title')),
        // No width: absorbs leftover % so the table stays full-bleed (GDrive-like).
      },
      {
        dataIndex: 'tags',
        key: 'tags',
        render: (_, record) => (
          <FilesTableTagsCell
            file={record}
            handleFileMenuAction={handleFileMenuAction}
            setSelectedTableKeys={setSelectedTableKeys}
          />
        ),
        responsive: FILES_TABLE_COLUMN_RESPONSIVE.tags,
        title: renderColumnTitle(t('common.tags')),
        width: columnWidths.tags,
      },
      {
        dataIndex: 'fileSize',
        key: 'fileSize',
        render: (_, record) => <FilesTableFileSizeCell file={record} />,
        responsive: FILES_TABLE_COLUMN_RESPONSIVE.fileSize,
        sorter: true,
        title: renderColumnTitle(t('common.fileSize')),
        width: columnWidths.fileSize,
      },
    ];

    if (showUserColumns) {
      columns.push(
        {
          dataIndex: 'createdBy',
          key: 'createdBy',
          render: (_, record) => <FilesTableCreatedByCell file={record} />,
          responsive: FILES_TABLE_COLUMN_RESPONSIVE.createdBy,
          title: renderColumnTitle(t('common.createdBy')),
          width: FILES_TABLE_COLUMN_WIDTH_PERCENT.withUsers.user,
        },
        {
          dataIndex: 'sharedBy',
          key: 'sharedBy',
          render: (_, record) => <FilesTableSharedByCell file={record} />,
          responsive: FILES_TABLE_COLUMN_RESPONSIVE.sharedBy,
          title: renderColumnTitle(t('common.sharedBy')),
          width: FILES_TABLE_COLUMN_WIDTH_PERCENT.withUsers.user,
        },
      );
    }

    if (isTrashTable) {
      columns.push({
        dataIndex: 'deletedAt',
        key: 'deletedAt',
        render: (_, record) => (
          <div className="relative flex min-w-0 items-center">
            <FilesTableDeletionDateCell file={record} />
            <FilesTableActionsCell file={record} {...actionsCellProps} />
          </div>
        ),
        responsive: FILES_TABLE_COLUMN_RESPONSIVE.deletionDate,
        sorter: true,
        title: renderColumnTitle(t('common.deletionDate')),
        width: columnWidths.date,
      });
    } else {
      columns.push({
        dataIndex: 'lastModified',
        key: 'lastModified',
        render: (_, record) => (
          <div className="relative flex min-w-0 items-center">
            <FilesTableLastModifiedCell file={record} />
            <FilesTableActionsCell file={record} {...actionsCellProps} />
          </div>
        ),
        responsive: FILES_TABLE_COLUMN_RESPONSIVE.lastModified,
        sorter: true,
        title: renderColumnTitle(t('common.lastModified')),
        width: columnWidths.date,
      });
    }

    return columns;
  }, [
    filesOffline,
    handleFileMenuAction,
    handleRowContextMenu,
    selectedTableKeys,
    setSelectedTableKeys,
    t,
    table,
    topBarFileActions,
  ]);
}
